import React, { useState, useRef, useEffect } from 'react';
import './VoiceRecorder.css';

const VoiceRecorder = ({ onRecordingComplete, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioURL, setAudioURL] = useState(null);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setError(null);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          // Max 5 minutes
          if (prev >= 300) {
            stopRecording();
            return 300;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setRecordingTime(0);
    setAudioURL(null);
    audioChunksRef.current = [];
    onCancel();
  };

  const sendRecording = async () => {
    if (audioURL && audioChunksRef.current.length > 0) {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
      
      // Generate simple waveform data (simplified for demo)
      const waveform = Array.from({ length: 50 }, () => Math.random() * 0.8 + 0.2);
      
      onRecordingComplete({
        blob: audioBlob,
        duration: recordingTime,
        waveform
      });
      
      // Reset
      setRecordingTime(0);
      setAudioURL(null);
      audioChunksRef.current = [];
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-recorder">
      {error && (
        <div className="voice-recorder-error">
          {error}
        </div>
      )}
      
      <div className="voice-recorder-content">
        {!isRecording && !audioURL ? (
          <div className="voice-recorder-start">
            <button onClick={startRecording} className="voice-record-btn">
              🎤 Start Recording
            </button>
            <button onClick={onCancel} className="voice-cancel-btn">
              Cancel
            </button>
          </div>
        ) : isRecording ? (
          <div className="voice-recorder-active">
            <div className="voice-recording-indicator">
              <span className="recording-dot"></span>
              <span className="recording-text">Recording...</span>
            </div>
            <div className="voice-recording-time">{formatTime(recordingTime)}</div>
            <div className="voice-recording-controls">
              <button onClick={pauseRecording} className="voice-pause-btn">
                {isPaused ? '▶️ Resume' : '⏸ Pause'}
              </button>
              <button onClick={stopRecording} className="voice-stop-btn">
                ⏹ Stop
              </button>
              <button onClick={cancelRecording} className="voice-cancel-btn">
                ✕ Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="voice-recorder-preview">
            <div className="voice-preview-header">
              <span>🎤 Voice Message</span>
              <span className="voice-preview-duration">{formatTime(recordingTime)}</span>
            </div>
            <audio src={audioURL} controls className="voice-preview-player" />
            <div className="voice-preview-controls">
              <button onClick={sendRecording} className="voice-send-btn">
                ✈️ Send
              </button>
              <button onClick={cancelRecording} className="voice-cancel-btn">
                🗑️ Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceRecorder;
